import { useState } from 'react';
import { Box, Typography, Button, Avatar, Paper, Rating, Link, Popover, IconButton, Tooltip, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import IosShareIcon from '@mui/icons-material/IosShare';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import { ColorButton } from '../../common/helpers';

const PageSummary = ({ title, rating, ownerAvatar, ownerName, ownerId, isFavorite, onToggleFavorite, onQuote, isOwner }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);

  const shareUrl = window.location.href;
  const shareText = `Mira esta página de servicios: ${title}`;

  const handleShare = async (e) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: shareUrl });
      } catch (_) {}
    } else {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const socialLinks = [
    {
      label: 'WhatsApp',
      icon: <WhatsAppIcon sx={{ color: '#25D366' }} />,
      url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`
    },
    {
      label: 'Facebook',
      icon: <FacebookIcon sx={{ color: '#1877F2' }} />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    },
    {
      label: 'X (Twitter)',
      icon: <XIcon sx={{ color: '#000' }} />,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
    }
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        width: '95%',
        maxWidth: '1200px',
        mx: 'auto',
        p: { xs: 2, md: 3 },
        borderRadius: '16px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 2,
        backgroundColor: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        gap: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#0E2465',
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}
        >
          {title}
        </Typography>

        {Number(rating) > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#F8FAFC', p: '4px 12px', borderRadius: '20px' }}>
            <Rating value={Number(rating)} precision={0.1} readOnly size="small" sx={{ color: '#0E2465' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0E2465' }}>
              {Number(rating).toFixed(1)}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#F8FAFC', p: '4px 12px', borderRadius: '20px' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#94A3B8' }}>
              Nuevo
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 4 }, flexWrap: 'wrap' }}>
        <Link
          component={RouterLink}
          to={`/perfil/${ownerId}`}
          underline="none"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: ownerId ? 'pointer' : 'default',
            '&:hover': { opacity: ownerId ? 0.8 : 1 }
          }}
        >
          <Avatar
            src={ownerAvatar}
            sx={{ width: 40, height: 40, border: '2px solid #E2E8F0' }}
          />
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#475569' }}>
            {ownerName}
          </Typography>
        </Link>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="text"
            color="primary"
            onClick={onToggleFavorite}
            sx={{ minWidth: 'auto', p: 1, '&:hover': { bgcolor: '#F1F5F9' } }}
          >
            {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </Button>

          <Button
            variant="contained"
            startIcon={<IosShareIcon />}
            onClick={handleShare}
            sx={{
              textTransform: 'none',
              bgcolor: '#0E2465',
              borderRadius: '8px',
              px: 3,
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            Compartir servicio
          </Button>

          <Button
            variant="outlined"
            onClick={onQuote}
            disabled={isOwner}
            sx={{
              textTransform: 'none',
              borderColor: '#E2E8F0',
              color: '#475569',
              borderRadius: '8px',
              px: 3,
              '&.Mui-disabled': { borderColor: '#E2E8F0', color: '#94A3B8' }
            }}
          >
            Cotizar
          </Button>
        </Box>
      </Box>

      {/* Popover fallback para navegadores sin Web Share API */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: { borderRadius: '12px', p: 2, width: 280, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="#0E2465" sx={{ mb: 1.5 }}>
          Compartir
        </Typography>

        {/* Copiar enlace */}
        <Box
          onClick={handleCopy}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: '8px 12px', borderRadius: '8px', cursor: 'pointer',
            border: '1px solid #E2E8F0', mb: 2,
            '&:hover': { bgcolor: '#F8FAFC' }
          }}
        >
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="caption" color="#94A3B8" display="block">Enlace</Typography>
            <Typography variant="body2" color="#475569" noWrap>{shareUrl}</Typography>
          </Box>
          {copied
            ? <CheckIcon sx={{ color: '#22C55E', fontSize: 20, flexShrink: 0 }} />
            : <ContentCopyIcon sx={{ color: '#94A3B8', fontSize: 20, flexShrink: 0 }} />
          }
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Redes sociales */}
        <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
          {socialLinks.map(({ label, icon, url }) => (
            <Tooltip key={label} title={label} placement="top">
              <IconButton
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  border: '1px solid #E2E8F0', borderRadius: '8px',
                  p: 1.5, '&:hover': { bgcolor: '#F8FAFC' }
                }}
              >
                {icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </Popover>
    </Paper>
  );
};

export default PageSummary;
